import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Plus, X, Zap, Lock, Sparkles, Users, Briefcase, Award, Layers,
  Target, Trophy, ArrowRight, ChevronDown, ChevronUp, Loader2, Trash2,
  MessageCircle, Dot,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const KIND_ICON = {
  mentor: Award,
  persona: Users,
  gtm: Layers,
  investor_scout: Briefcase,
  growth: Target,
};

export default function AgentsView({ onRefresh }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [tab, setTab] = useState('ventures'); // 'ventures' | 'core'
  const [openVenture, setOpenVenture] = useState(null);
  const [deployTarget, setDeployTarget] = useState(null); // agent pending venture pick

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/agents/all`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setData(d);
        if (!openVenture && d.ventures?.length) setOpenVenture(d.ventures[0].monetize_id);
      }
    } finally { setLoading(false); }
  }, [openVenture]);

  useEffect(() => { load(); }, [load]);

  const toggleCore = async (agent, activating, payload) => {
    setBusy(b => ({ ...b, [agent.agent_id]: activating ? 'activating' : 'deactivating' }));
    try {
      const ep = activating
        ? `${API}/api/agents/${agent.agent_id}/activate`
        : `${API}/api/agents/${agent.agent_id}/deactivate`;
      const init = { method: 'POST', credentials: 'include' };
      if (activating && payload && typeof payload === 'object') {
        init.headers = { 'Content-Type': 'application/json' };
        init.body = JSON.stringify(payload);
      }
      const r = await fetch(ep, init);
      if (r.ok) { await load(); onRefresh?.(); }
    } finally {
      setBusy(b => { const c = { ...b }; delete c[agent.agent_id]; return c; });
    }
  };

  // User clicked "Deploy agent" on an inactive specialist → open the venture picker.
  // Free agents skip the modal entirely (they're system-wide).
  const requestDeploy = (agent) => {
    if (agent.free) {
      toggleCore(agent, true);
      return;
    }
    setDeployTarget(agent);
  };

  const removeProjectAgent = async (agent) => {
    setBusy(b => ({ ...b, [agent.agent_id]: 'removing' }));
    try {
      const r = await fetch(`${API}/api/agents/project/${agent.agent_id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (r.ok) await load();
    } finally {
      setBusy(b => { const c = { ...b }; delete c[agent.agent_id]; return c; });
    }
  };

  if (loading || !data) {
    return (
      <div style={{ flex: 1, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(15,15,30,0.48)', fontSize: 12 }}>
        Loading your team…
      </div>
    );
  }

  const stats = data.stats || {};

  return (
    <div data-testid="agents-view" style={{
      flex: 1, height: '100%', overflowY: 'auto', color: '#0F0F1A',
      background: 'linear-gradient(180deg, #FBFAF7 0%, #F4F3F0 100%)',
    }}>
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 36px 60px' }}>

        {/* Hero */}
        <div style={{
          display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
          gap: 20, marginBottom: 24,
        }}>
          <div>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 9,
              fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase',
              color: '#7C3AED', padding: '4px 8px',
              background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.3)',
            }}>
              <Sparkles size={11} /> Autonomous Workforce
            </span>
            <h1 style={{ fontSize: 30, fontWeight: 800, margin: '12px 0 6px', letterSpacing: '-0.015em' }}>
              Your AI team
            </h1>
            <p style={{ fontSize: 13, color: 'rgba(15,15,30,0.6)', maxWidth: 620, lineHeight: 1.6, margin: 0 }}>
              Core specialists work across all your projects. Each venture also spawns its own crew —
              mentor, personas, GTM expert, investor scout — that knows the deck, the buyers and the funnel intimately.
            </p>
          </div>
          <StatStrip stats={stats} />
        </div>

        {/* Tabs */}
        <div data-testid="agents-tabs" style={{
          display: 'inline-flex', gap: 0, marginBottom: 20,
          background: 'rgba(15,15,30,0.03)',
          border: '1px solid rgba(15,15,30,0.06)',
          padding: 3,
        }}>
          {[
            { id: 'ventures', label: `Venture teams · ${stats.ventures_count || 0}`, accent: '#FFD700' },
            { id: 'core', label: `Core team · ${stats.core_active}/${stats.core_total}`, accent: '#7C3AED' },
          ].map(t => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`agents-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                style={{
                  padding: '8px 16px', fontFamily: 'inherit', cursor: 'pointer',
                  fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                  background: active ? `${t.accent}1f` : 'transparent',
                  color: active ? t.accent : 'rgba(15,15,30,0.55)',
                  border: active ? `1px solid ${t.accent}40` : '1px solid transparent',
                  transition: 'all 0.15s',
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === 'ventures' ? (
          <VenturesPanel
            ventures={data.ventures || []}
            openVenture={openVenture}
            setOpenVenture={setOpenVenture}
            busy={busy}
            onRemove={removeProjectAgent}
          />
        ) : (
          <CorePanel
            core={data.core || []}
            ventures={data.ventures || []}
            busy={busy}
            onToggle={toggleCore}
            onDeployRequest={requestDeploy}
          />
        )}

      </div>

      {/* Project picker modal */}
      {deployTarget && (
        <DeployModal
          agent={deployTarget}
          ventures={data.ventures || []}
          busy={busy[deployTarget.agent_id]}
          onClose={() => setDeployTarget(null)}
          onConfirm={async (payload) => {
            await toggleCore(deployTarget, true, payload);
            setDeployTarget(null);
          }}
        />
      )}
    </div>
  );
}

/* =================== Stat strip =================== */
function StatStrip({ stats }) {
  const items = [
    { label: 'Specialists', value: stats.core_active || 0, color: '#7C3AED', icon: <Users size={12} /> },
    { label: 'Ventures', value: stats.ventures_count || 0, color: '#FFD700', icon: <Trophy size={12} /> },
    { label: 'Project agents', value: stats.project_agents_count || 0, color: '#A855F7', icon: <Sparkles size={12} /> },
  ];
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      {items.map(it => (
        <div key={it.label} style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 14px',
          background: `${it.color}10`,
          border: `1px solid ${it.color}30`,
          minWidth: 130,
        }}>
          <span style={{ color: it.color, display: 'flex' }}>{it.icon}</span>
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(15,15,30,0.5)' }}>
              {it.label}
            </div>
            <div style={{ fontSize: 16, fontWeight: 800, color: it.color, fontFamily: "'JetBrains Mono', monospace", marginTop: 1 }}>
              {it.value}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* =================== Venture teams panel =================== */
function VenturesPanel({ ventures, openVenture, setOpenVenture, busy, onRemove }) {
  if (!ventures.length) {
    return (
      <div style={{
        padding: 60, textAlign: 'center',
        background: 'rgba(255,215,0,0.03)',
        border: '1px dashed rgba(255,215,0,0.25)',
      }}>
        <Trophy size={36} color="rgba(255,215,0,0.6)" style={{ margin: '0 auto 16px', display: 'block' }} />
        <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 6 }}>No venture teams yet</div>
        <div style={{ fontSize: 12, color: 'rgba(15,15,30,0.6)', maxWidth: 520, margin: '0 auto', lineHeight: 1.6 }}>
          Promote a project to <strong style={{ color: '#FFD700' }}>Monetize</strong> and run the funnel.
          Every module you generate spawns a personalized agent here — mentor after the dataroom,
          buyers after the personas, a GTM expert after the business model, and a capital scout after the investor list.
        </div>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {ventures.map(v => (
        <VentureBlock
          key={v.monetize_id}
          v={v}
          open={openVenture === v.monetize_id}
          onToggle={() => setOpenVenture(openVenture === v.monetize_id ? null : v.monetize_id)}
          busy={busy}
          onRemove={onRemove}
        />
      ))}
    </div>
  );
}

function VentureBlock({ v, open, onToggle, busy, onRemove }) {
  const hasFirstDollar = !!v.first_dollar_at;
  const accent = hasFirstDollar ? '#22C55E' : '#FFD700';
  // Pinned core (Aria, Hermes) + deployed specialists (Marcus, Iris, etc.) all
  // share the venture-level core roster. Project-spawned agents (Mentor,
  // personas, GTM expert, …) are listed separately below.
  const pinnedCore = useMemo(
    () => (v.agents || []).filter(a => a.kind === 'core' || a.kind === 'specialist'),
    [v.agents],
  );
  const projectAgents = useMemo(
    () => (v.agents || []).filter(a => a.kind !== 'core' && a.kind !== 'specialist'),
    [v.agents],
  );
  const grouped = useMemo(() => groupAgentsByKind(projectAgents), [projectAgents]);
  const totalAgents = (v.agents || []).length;

  return (
    <div data-testid={`venture-block-${v.monetize_id}`} style={{
      background: 'rgba(15,15,30,0.025)',
      border: `1px solid ${open ? `${accent}55` : 'rgba(15,15,30,0.06)'}`,
      transition: 'all 0.2s',
    }}>
      {/* Header */}
      <button
        onClick={onToggle}
        data-testid={`venture-toggle-${v.monetize_id}`}
        style={{
          width: '100%', padding: 18,
          display: 'flex', alignItems: 'center', gap: 16,
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: '#0F0F1A', fontFamily: 'inherit', textAlign: 'left',
        }}
      >
        <div style={{
          width: 44, height: 44, flexShrink: 0,
          background: `${accent}1c`, border: `1px solid ${accent}55`,
          color: accent,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {hasFirstDollar ? <Trophy size={20} /> : <Target size={20} />}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ fontSize: 15, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {v.name}
            </div>
            {hasFirstDollar && (
              <span style={{
                fontSize: 8, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                color: '#22C55E', padding: '2px 6px',
                background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)',
              }}>First $ collected</span>
            )}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(15,15,30,0.55)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              {pinnedCore.length} core
              {projectAgents.length > 0 ? ` + ${projectAgents.length} project` : ''}
            </span>
            <Dot size={10} />
            <span>score {v.score}</span>
            <Dot size={10} />
            <span>{(v.modules_complete || []).length} modules</span>
          </div>
        </div>

        {/* Avatar stack */}
        <div style={{ display: 'flex', alignItems: 'center', marginRight: 8 }}>
          {v.agents.slice(0, 5).map((a, i) => (
            <div key={a.agent_id} style={{
              width: 28, height: 28, marginLeft: i === 0 ? 0 : -8,
              background: `${a.color}25`, color: a.color,
              border: `2px solid #FBFAF7`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 800, zIndex: 5 - i,
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              {a.avatar || a.name.slice(0, 1)}
            </div>
          ))}
          {v.agents.length > 5 && (
            <div style={{
              width: 28, height: 28, marginLeft: -8,
              background: 'rgba(15,15,30,0.06)', color: 'rgba(15,15,30,0.65)',
              border: '2px solid #FBFAF7',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 9, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace",
            }}>
              +{v.agents.length - 5}
            </div>
          )}
        </div>

        {open ? <ChevronUp size={16} color="rgba(15,15,30,0.55)" /> : <ChevronDown size={16} color="rgba(15,15,30,0.55)" />}
      </button>

      {/* Body */}
      {open && (
        <div style={{ padding: '0 18px 18px', borderTop: '1px solid rgba(15,15,30,0.035)' }}>
          {totalAgents === 0 ? (
            <div style={{
              padding: '24px 0', textAlign: 'center',
              fontSize: 12, color: 'rgba(15,15,30,0.5)', lineHeight: 1.6,
            }}>
              No agents on this venture yet. Generate the <strong style={{ color: '#FFD700' }}>Dataroom</strong> in Monetize
              to spawn a Mentor, then Personas → buyer agents, Business Models → GTM expert, Investors → capital scout.
            </div>
          ) : (
            <KindGroups groups={grouped} accent={accent} busy={busy} onRemove={onRemove} coreAgents={pinnedCore} />
          )}
        </div>
      )}
    </div>
  );
}

function KindGroups({ groups, accent, busy, onRemove, coreAgents }) {
  const order = ['mentor', 'persona', 'gtm', 'investor_scout', 'growth'];
  const labels = {
    mentor: 'Mentor',
    persona: 'Buyer personas',
    gtm: 'Go-to-market',
    investor_scout: 'Capital scout',
    growth: 'Growth',
  };
  const hasCore = (coreAgents || []).length > 0;
  const hasProject = order.some(k => groups[k]?.length);
  return (
    <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 22 }}>
      {/* Pinned core team — always present (Aria + Hermes + deployed paid core) */}
      {hasCore && (
        <div>
          <div style={{
            fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
            color: 'rgba(15,15,30,0.55)', marginBottom: 10,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span>Core team on this venture</span>
            <span style={{ padding: '1px 6px', background: 'rgba(124,58,237,0.10)', color: '#6D28D9', fontSize: 9, fontFamily: "'JetBrains Mono', monospace", border: '1px solid rgba(124,58,237,0.25)' }}>
              {coreAgents.length} pinned
            </span>
            <div style={{ flex: 1, height: 1, background: 'linear-gradient(to right, rgba(124,58,237,0.18), transparent)' }} />
          </div>
          <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
            {coreAgents.map(a => <CoreAgentChip key={a.agent_id} agent={a} />)}
          </div>
        </div>
      )}

      {/* Project-specific agents spawned via Monetize */}
      {hasProject && order.filter(k => groups[k]?.length).map(kind => (
        <div key={kind}>
          <div style={{
            fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
            color: 'rgba(15,15,30,0.55)', marginBottom: 10,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span>{labels[kind] || kind}</span>
            <span style={{ padding: '1px 6px', background: 'rgba(15,15,30,0.05)', color: 'rgba(15,15,30,0.6)', fontSize: 9, fontFamily: "'JetBrains Mono', monospace" }}>
              {groups[kind].length}
            </span>
            <div style={{ flex: 1, height: 1, background: 'linear-gradient(to right, rgba(15,15,30,0.08), transparent)' }} />
          </div>
          <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
            {groups[kind].map(a => (
              <ProjectAgentCard
                key={a.agent_id}
                agent={a}
                busy={busy[a.agent_id]}
                onRemove={() => onRemove(a)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* Compact chip for core agents pinned to a venture (read-only — they're managed
   from the Core team tab, never removable from a venture). */
function CoreAgentChip({ agent }) {
  return (
    <div data-testid={`venture-core-${agent.agent_id}`} style={{
      padding: 14,
      background: '#FFFFFF',
      border: `1px solid ${agent.color}30`,
      boxShadow: '0 1px 2px rgba(15,15,30,0.04)',
      display: 'flex', alignItems: 'center', gap: 12,
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', top: 0, right: 0, width: 70, height: 70,
        background: `radial-gradient(circle at top right, ${agent.color}1f, transparent 65%)`,
        pointerEvents: 'none',
      }} />
      <div style={{
        width: 36, height: 36, flexShrink: 0,
        background: `${agent.color}22`, color: agent.color,
        border: `1px solid ${agent.color}55`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 800, fontSize: 13,
        fontFamily: "'JetBrains Mono', monospace",
        position: 'relative',
      }}>
        {(agent.name || 'A').slice(0, 1).toUpperCase()}
        <span style={{
          position: 'absolute', bottom: -3, right: -3,
          width: 10, height: 10, background: '#22C55E',
          border: '2px solid #FFFFFF',
          animation: 'pulse-glow 1.8s ease-in-out infinite',
        }} />
      </div>
      <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 13, fontWeight: 700, color: '#0F0F1A',
          letterSpacing: '-0.005em',
        }}>
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{agent.name}</span>
          {agent.free ? (
            <span style={{
              fontSize: 8, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
              color: '#6D28D9', padding: '1px 6px',
              background: 'rgba(124,58,237,0.10)', border: '1px solid rgba(124,58,237,0.25)',
            }}>Free</span>
          ) : (
            <span style={{
              fontSize: 8, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
              color: '#B8860B', padding: '1px 6px',
              background: 'rgba(255,215,0,0.14)', border: '1px solid rgba(255,215,0,0.4)',
            }}>Specialist</span>
          )}
        </div>
        <div style={{ fontSize: 10.5, color: 'rgba(15,15,30,0.55)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {agent.role}
        </div>
      </div>
      <Lock size={12} style={{ color: 'rgba(15,15,30,0.32)' }} />
    </div>
  );
}

function ProjectAgentCard({ agent, busy, onRemove }) {
  const Icon = KIND_ICON[agent.kind] || Sparkles;
  const meta = agent.meta || {};
  return (
    <div data-testid={`project-agent-${agent.agent_id}`} style={{
      padding: 14,
      background: 'rgba(15,15,30,0.025)',
      border: `1px solid ${agent.color}25`,
      position: 'relative', overflow: 'hidden',
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{
        position: 'absolute', top: 0, right: 0, width: 80, height: 80,
        background: `radial-gradient(circle at top right, ${agent.color}18, transparent 65%)`,
        pointerEvents: 'none',
      }} />
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, position: 'relative' }}>
        <div style={{
          width: 36, height: 36, flexShrink: 0,
          background: `${agent.color}20`, color: agent.color,
          border: `1px solid ${agent.color}45`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 800, fontSize: 13,
          fontFamily: "'JetBrains Mono', monospace",
          position: 'relative',
        }}>
          {agent.avatar || agent.name.slice(0, 2).toUpperCase()}
          <span style={{
            position: 'absolute', bottom: -3, right: -3,
            width: 10, height: 10, background: '#22C55E',
            border: '2px solid #FBFAF7',
            animation: 'pulse-glow 1.8s ease-in-out infinite',
          }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontSize: 8, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
            color: agent.color, marginBottom: 3,
          }}>
            <Icon size={9} /> {agent.kind_label || agent.kind}
          </div>
          <div style={{
            fontSize: 13, fontWeight: 700, color: '#0F0F1A',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {agent.name}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(15,15,30,0.6)', marginTop: 1 }}>
            {agent.role}
          </div>
        </div>
        <button
          onClick={onRemove}
          disabled={!!busy}
          data-testid={`project-agent-remove-${agent.agent_id}`}
          title="Remove agent"
          style={{
            width: 22, height: 22, flexShrink: 0,
            background: 'transparent', border: '1px solid rgba(15,15,30,0.08)',
            color: 'rgba(15,15,30,0.48)', cursor: busy ? 'wait' : 'pointer',
            fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.color = '#EF4444'; e.currentTarget.style.borderColor = '#EF444455'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'rgba(15,15,30,0.48)'; e.currentTarget.style.borderColor = 'rgba(15,15,30,0.08)'; }}
        >
          {busy ? <Loader2 size={10} style={{ animation: 'spin 0.7s linear infinite' }} /> : <Trash2 size={10} />}
        </button>
      </div>

      {agent.tagline && (
        <div style={{
          fontSize: 11, color: 'rgba(15,15,30,0.7)', lineHeight: 1.55,
          position: 'relative',
        }}>
          {agent.tagline}
        </div>
      )}

      {/* Persona quote */}
      {agent.kind === 'persona' && meta.feedback_quote && (
        <div style={{
          fontSize: 11, fontStyle: 'italic', color: 'rgba(15,15,30,0.72)',
          padding: '8px 10px',
          background: 'rgba(15,15,30,0.025)',
          borderLeft: `3px solid ${agent.color}`,
          lineHeight: 1.5, position: 'relative',
        }}>
          &ldquo;{meta.feedback_quote}&rdquo;
        </div>
      )}

      {/* Meta chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, position: 'relative' }}>
        {agent.kind === 'persona' && typeof meta.score === 'number' && (
          <Chip color={meta.score >= 75 ? '#22C55E' : meta.score >= 50 ? agent.color : '#FF6B6B'}>
            score {meta.score}
          </Chip>
        )}
        {agent.kind === 'persona' && meta.willingness_to_pay && (
          <Chip color="#22C55E">WTP {meta.willingness_to_pay}</Chip>
        )}
        {agent.kind === 'gtm' && meta.recommended_model && (
          <Chip color={agent.color}>{meta.recommended_model}</Chip>
        )}
        {agent.kind === 'investor_scout' && typeof meta.count === 'number' && (
          <Chip color={agent.color}>{meta.count} funds tracked</Chip>
        )}
      </div>

      {/* Future: Open chat */}
      <button
        disabled
        title="Live agent chat — coming soon"
        style={{
          marginTop: 4, padding: '7px 10px', fontSize: 10, fontWeight: 700,
          background: 'rgba(15,15,30,0.03)', color: 'rgba(15,15,30,0.5)',
          border: '1px solid rgba(15,15,30,0.06)', cursor: 'not-allowed',
          fontFamily: 'inherit', letterSpacing: '0.05em', textTransform: 'uppercase',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        }}
      >
        <MessageCircle size={10} /> Open dialogue · soon
      </button>
    </div>
  );
}

function Chip({ color, children }) {
  return (
    <span style={{
      fontSize: 9, padding: '3px 7px', fontWeight: 700,
      background: `${color}1f`, color, border: `1px solid ${color}40`,
      letterSpacing: '0.04em', textTransform: 'uppercase',
      fontFamily: "'JetBrains Mono', monospace",
    }}>
      {children}
    </span>
  );
}

function groupAgentsByKind(agents) {
  const out = {};
  for (const a of agents || []) {
    const k = a.kind || 'other';
    if (!out[k]) out[k] = [];
    out[k].push(a);
  }
  return out;
}

/* =================== Core panel =================== */
function CorePanel({ core, ventures, busy, onToggle, onDeployRequest }) {
  // 3 elite agents — Aria · Lyra · Hermes — the Build / Package / Sell trinity.
  // Hermes owns the SELL pillar end-to-end (lead-gen + outreach + closing),
  // absorbing the former Markus persona. No more "Specialists" or "Available"
  // sections: the core team is fixed, always-on, locked to the studio.
  // (See /app/memory/AGENT_RUNTIME.md §5.)
  return <CoreTeamHero core={core} busy={busy} onToggle={onToggle} />;
}

/* =================== Core Team Hero — 3 elite cards ===================
   The 3 minds running every venture — the Build / Package / Sell trinity.
   Editorial trading-card layout. Hard-coded specs ensure visual
   consistency across HQ + Agents.
   ====================================================================== */
const CORE_TEAM_SPECS = [
  {
    key: 'builder',
    name: 'Aria',
    role: 'The Architect',
    quote: 'I construct the product, line by line.',
    accent: '#7C3AED',
    accentSoft: '#A78BFA',
    gradient: 'linear-gradient(135deg, #A78BFA 0%, #7C3AED 100%)',
    img: 'https://customer-assets.emergentagent.com/job_emergent-nextjs-llm/artifacts/oqa19gtc_77de9bac-3f25-4df6-83c9-1bfd45a5acfe.png',
    responsibilities: [
      'Tech research', 'Architecture', 'UI/UX', 'Code generation',
      'Testing', 'Deployment', 'Bug fixes', 'API integrations', 'Database design',
    ],
    tools: ['GitHub', 'Supabase', 'Lovable', 'Cursor', 'Vercel', 'Railway', 'Firecrawl', 'Context7'],
    examples: ['Ship a SaaS', 'Spin up a marketplace', 'Build a CRM', 'Launch a mobile app', 'Deploy to production'],
    matchNames: ['aria', 'builder'],
  },
  {
    key: 'content',
    name: 'Lyra',
    role: 'The Storyteller',
    quote: 'I shape every word, image, and frame.',
    accent: '#FF6B1A',
    accentSoft: '#FF9F40',
    gradient: 'linear-gradient(135deg, #FFD166 0%, #FF6B1A 100%)',
    img: 'https://customer-assets.emergentagent.com/job_emergent-nextjs-llm/artifacts/a3f3fe57_2f141ba8-6ea7-4405-9fd5-d1d34fa09a9f.png',
    responsibilities: [
      'Editorial strategy', 'Content calendar', 'Copywriting', 'Image creation',
      'Video creation', 'UGC', 'Newsletters', 'Social media',
      'Visual competitive intel', 'Brand consistency audit',
    ],
    tools: ['GPT', 'Claude', 'Nano Banana', 'Runway', 'Veo', 'ElevenLabs', 'Canva'],
    examples: ['30 LinkedIn posts', '100 TikToks', 'Instagram campaign', 'Landing page copy', 'Ad creatives'],
    matchNames: ['lyra', 'content', 'storyteller', 'editor', 'writer'],
  },
  {
    key: 'growth',
    name: 'Hermes',
    role: 'The Pathfinder',
    quote: 'I find the right humans and bring them home.',
    accent: '#FFB347',
    accentSoft: '#FFD166',
    gradient: 'linear-gradient(135deg, #FFE89D 0%, #FFB347 100%)',
    img: 'https://customer-assets.emergentagent.com/job_emergent-nextjs-llm/artifacts/mou7csvz_238e23e8-fd0c-4b2b-ae70-8b1eb646daec.png',
    responsibilities: [
      // SELL pillar = unified lead-gen + outreach + closing (absorbed Markus).
      'Market analysis', 'Competitive sales intel', 'Positioning', 'ICP definition',
      'Scraping', 'Lead generation', 'Lead enrichment',
      'Cold email', 'Cold calling', 'Follow-up', 'Closing',
      'SEO', 'Paid ads', 'Conversion optimization', 'CRM hygiene',
    ],
    tools: ['LeadMagic', 'Apollo', 'Clay', 'Firecrawl', 'Context.dev', 'SimilarWeb', 'Ahrefs', 'Semrush', 'Meta Ads', 'Google Ads', 'Google Analytics', 'HubSpot', 'Gmail', 'Twilio', 'Calendly'],
    examples: ['Map the best channels', 'Find 500 prospects', 'Enrich every email', 'Run cold sequences', 'Auto-dial outbound', 'Book meetings', 'Decode competitors', 'Engineer a funnel'],
    matchNames: ['hermes', 'growth', 'markus', 'marcus', 'revenue', 'sales'],
  },
];

function CoreTeamHero({ core, busy, onToggle }) {
  // Try to match each spec to a real backend agent so toggle keeps working.
  const findAgent = (spec) => {
    const list = core || [];
    return list.find(a => {
      const id = (a.agent_id || '').toLowerCase();
      const name = (a.name || '').toLowerCase();
      return spec.matchNames.some(m => id.includes(m) || name.includes(m));
    });
  };

  return (
    <div style={{ marginTop: 4 }}>
      {/* Section header — premium editorial eyebrow */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14,
        marginBottom: 22, padding: '0 2px',
      }}>
        <span style={{
          fontSize: 10, fontWeight: 800, letterSpacing: '0.32em',
          textTransform: 'uppercase', color: '#7C3AED',
        }}>
          The Core Team
        </span>
        <span style={{
          fontSize: 11, color: 'rgba(15,15,30,0.45)',
          fontFamily: "'JetBrains Mono', monospace",
          letterSpacing: '0.04em',
        }}>
          · the trinity that runs every venture · Build · Package · Sell
        </span>
        <div style={{ flex: 1, height: 1, background: 'linear-gradient(to right, rgba(15,15,30,0.10), transparent)' }} />
        <span style={{
          padding: '4px 10px',
          background: 'rgba(124,58,237,0.10)',
          border: '1px solid rgba(124,58,237,0.25)',
          fontSize: 10, fontWeight: 700,
          color: '#7C3AED',
          fontFamily: "'JetBrains Mono', monospace",
          letterSpacing: '0.06em',
        }}>
          3 · ALWAYS ON
        </span>
      </div>

      {/* 4 cards — 2x2 on desktop, 1 column on mobile */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
        gap: 18,
      }}>
        {CORE_TEAM_SPECS.map(spec => {
          const agent = findAgent(spec);
          const isActive = agent ? !!agent.active : true;
          return (
            <CoreTeamCard
              key={spec.key}
              spec={spec}
              agentId={agent?.agent_id}
              isActive={isActive}
              busy={agent ? busy[agent.agent_id] : null}
              onToggle={agent ? () => onToggle(agent, !isActive) : null}
            />
          );
        })}
      </div>
    </div>
  );
}

function CoreTeamCard({ spec, agentId, isActive, busy, onToggle }) {
  const [openTab, setOpenTab] = useState('responsibilities');
  const tabs = [
    { key: 'responsibilities', label: 'Responsibilities', items: spec.responsibilities },
    { key: 'tools', label: 'Tools', items: spec.tools },
    { key: 'examples', label: 'Examples', items: spec.examples },
  ];
  const active = tabs.find(t => t.key === openTab) || tabs[0];

  return (
    <div
      data-testid={`core-team-card-${spec.key}`}
      style={{
        position: 'relative',
        background: '#FFFFFF',
        border: '1px solid rgba(15,15,30,0.07)',
        borderRadius: 22,
        overflow: 'hidden',
        transition: 'transform 0.3s, box-shadow 0.3s, border-color 0.3s',
        boxShadow: '0 1px 2px rgba(15,15,30,0.04)',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.boxShadow = `0 22px 50px ${spec.accent}25`;
        e.currentTarget.style.borderColor = `${spec.accent}55`;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 1px 2px rgba(15,15,30,0.04)';
        e.currentTarget.style.borderColor = 'rgba(15,15,30,0.07)';
      }}
    >
      {/* Top color band — signature stripe */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 4,
        background: spec.gradient,
      }} />

      {/* Hero zone */}
      <div style={{
        position: 'relative',
        padding: '26px 24px 22px',
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        gap: 18,
        alignItems: 'center',
        borderBottom: '1px solid rgba(15,15,30,0.05)',
      }}>
        {/* Avatar */}
        <div style={{
          position: 'relative',
          width: 84, height: 84, borderRadius: '50%',
          padding: 3,
          background: spec.gradient,
        }}>
          <div style={{
            width: '100%', height: '100%',
            borderRadius: '50%',
            overflow: 'hidden',
            background: '#FFFFFF',
            padding: 2,
          }}>
            {spec.img ? (
              <img src={spec.img} alt={spec.name} style={{
                width: '100%', height: '100%',
                borderRadius: '50%',
                objectFit: 'cover',
                objectPosition: 'top center',
              }} />
            ) : (
              <div style={{
                width: '100%', height: '100%',
                borderRadius: '50%',
                background: spec.gradient,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#FFFFFF',
                fontSize: 32, fontWeight: 900,
                letterSpacing: '-0.04em',
                fontFamily: "'Great Vibes', cursive",
                textShadow: '0 2px 6px rgba(0,0,0,0.15)',
              }}>
                {spec.name[0]}
              </div>
            )}
          </div>
          {isActive && (
            <span style={{
              position: 'absolute', bottom: 2, right: 2,
              width: 14, height: 14, borderRadius: '50%',
              background: '#22C55E',
              border: '3px solid #FFFFFF',
              boxShadow: '0 0 10px #22C55E',
              animation: 'pulse-glow 1.8s ease-in-out infinite',
            }} />
          )}
        </div>

        {/* Identity */}
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontSize: 11, fontWeight: 800,
            color: spec.accent,
            textTransform: 'uppercase', letterSpacing: '0.22em',
            marginBottom: 4,
          }}>
            {spec.name}
          </div>
          <div style={{
            fontSize: 22, fontWeight: 900,
            letterSpacing: '-0.025em',
            background: spec.gradient,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            lineHeight: 1.05,
            marginBottom: 8,
          }}>
            {spec.role}
          </div>
          <div style={{
            position: 'relative',
            fontSize: 12,
            color: 'rgba(15,15,30,0.62)',
            fontStyle: 'italic',
            lineHeight: 1.5,
            paddingLeft: 14,
            borderLeft: `2px solid ${spec.accent}40`,
          }}>
            {spec.quote}
          </div>
        </div>

        {/* Always-on badge */}
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6,
        }}>
          <span style={{
            fontSize: 9, fontWeight: 800, letterSpacing: '0.18em',
            padding: '4px 9px',
            background: `${spec.accent}14`,
            border: `1px solid ${spec.accent}40`,
            color: spec.accent,
            textTransform: 'uppercase',
            borderRadius: 999,
          }}>
            Core · Always on
          </span>
        </div>
      </div>

      {/* Tabbed knowledge area */}
      <div style={{ padding: '18px 24px 24px' }}>
        {/* Tabs */}
        <div style={{
          display: 'flex', gap: 4,
          marginBottom: 14,
          padding: 4,
          background: 'rgba(15,15,30,0.035)',
          borderRadius: 10,
          width: 'fit-content',
        }}>
          {tabs.map(t => {
            const isOpen = t.key === openTab;
            return (
              <button
                key={t.key}
                data-testid={`core-team-tab-${spec.key}-${t.key}`}
                onClick={() => setOpenTab(t.key)}
                style={{
                  padding: '7px 13px',
                  border: 'none',
                  borderRadius: 7,
                  background: isOpen ? '#FFFFFF' : 'transparent',
                  color: isOpen ? spec.accent : 'rgba(15,15,30,0.55)',
                  fontSize: 10, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.12em',
                  cursor: 'pointer', fontFamily: 'inherit',
                  boxShadow: isOpen ? `0 2px 6px ${spec.accent}25` : 'none',
                  transition: 'background-color 0.2s, color 0.2s, box-shadow 0.2s',
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Pills cloud */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, minHeight: 84 }}>
          {active.items.map(item => (
            <span
              key={item}
              style={{
                fontSize: 11, fontWeight: 600,
                padding: '5px 11px',
                background: `${spec.accent}10`,
                border: `1px solid ${spec.accent}28`,
                color: 'rgba(15,15,30,0.78)',
                borderRadius: 999,
                fontFamily: openTab === 'tools' ? "'JetBrains Mono', monospace" : 'inherit',
                letterSpacing: openTab === 'tools' ? '-0.005em' : '0',
                transition: 'background-color 0.15s, border-color 0.15s, transform 0.15s',
                cursor: 'default',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = `${spec.accent}1f`;
                e.currentTarget.style.borderColor = `${spec.accent}55`;
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = `${spec.accent}10`;
                e.currentTarget.style.borderColor = `${spec.accent}28`;
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {item}
            </span>
          ))}
        </div>

        {/* Footer line */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginTop: 18, paddingTop: 14,
          borderTop: '1px solid rgba(15,15,30,0.05)',
        }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            fontSize: 10, color: 'rgba(15,15,30,0.5)',
            textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 700,
          }}>
            <Lock size={10} />
            Locked to your studio
          </div>
          {agentId ? (
            <span style={{
              fontSize: 10, fontWeight: 700,
              color: isActive ? '#22C55E' : 'rgba(15,15,30,0.45)',
              fontFamily: "'JetBrains Mono', monospace",
              letterSpacing: '0.08em',
            }}>
              {isActive ? '● ONLINE' : '○ STANDBY'}
            </span>
          ) : (
            <span style={{
              fontSize: 10, fontWeight: 700,
              color: spec.accent,
              fontFamily: "'JetBrains Mono', monospace",
              letterSpacing: '0.08em',
            }}>
              ● READY
            </span>
          )}
        </div>
      </div>
    </div>
  );
}


function SectionHeader({ label, count, accent }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      margin: '4px 0 14px',
      fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
      color: 'rgba(15,15,30,0.5)',
    }}>
      <span style={{ width: 6, height: 6, background: accent }} />
      <span>{label}</span>
      <span style={{
        padding: '2px 8px', background: 'rgba(15,15,30,0.06)',
        fontSize: 10, fontWeight: 600, color: 'rgba(15,15,30,0.65)',
      }}>
        {count}
      </span>
      <div style={{ flex: 1, height: 1, background: 'linear-gradient(to right, rgba(15,15,30,0.08), transparent)' }} />
    </div>
  );
}

function CoreCard({ agent, busy, onToggle, ventureLabel, projectKind, hasVenture }) {
  const isActive = agent.active;
  // Free agents (Aria, Hermes) ship with every account and cannot be removed.
  const isLocked = !!agent.free;
  return (
    <div data-testid={`core-card-${agent.agent_id}`} style={{
      position: 'relative', padding: 20,
      background: isActive
        ? 'linear-gradient(135deg, rgba(124,58,237,0.05) 0%, rgba(15,15,30,0.025) 100%)'
        : 'rgba(15,15,30,0.025)',
      border: isActive ? '1px solid rgba(124,58,237,0.3)' : '1px solid rgba(15,15,30,0.06)',
      transition: 'all 0.2s',
      overflow: 'hidden',
    }}>
      {isActive && (
        <div style={{
          position: 'absolute', top: 0, right: 0, width: 80, height: 80,
          background: `radial-gradient(circle at top right, ${agent.color}15, transparent 70%)`,
          pointerEvents: 'none',
        }} />
      )}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <div style={{
          width: 40, height: 40, flexShrink: 0,
          background: `${agent.color}18`, color: agent.color,
          border: `1px solid ${agent.color}40`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 800, fontSize: 14, position: 'relative',
        }}>
          {agent.name.split(' ').map(w => w[0]).join('').slice(0, 2)}
          {isActive && (
            <span style={{
              position: 'absolute', bottom: -2, right: -2,
              width: 10, height: 10, background: '#22C55E',
              border: '2px solid #FBFAF7',
              animation: 'pulse-glow 1.8s ease-in-out infinite',
            }} />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{agent.name}</div>
          <div style={{ fontSize: 11, color: 'rgba(15,15,30,0.55)', marginTop: 2 }}>{agent.role}</div>
        </div>
        {agent.free && (
          <span style={{
            fontSize: 9, fontWeight: 700, letterSpacing: '0.1em',
            padding: '3px 7px', color: '#7C3AED',
            background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.25)',
          }}>FREE</span>
        )}
      </div>
      {/* Project / venture badge — shows where this specialist is working */}
      {isActive && !agent.free && ventureLabel && (
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontSize: 10, fontWeight: 600, color: '#0F0F1A',
          padding: '4px 9px', marginBottom: 10,
          background: `${agent.color}14`,
          border: `1px solid ${agent.color}3a`,
          maxWidth: '100%',
        }}>
          {hasVenture
            ? <Trophy size={10} style={{ color: agent.color, flexShrink: 0 }} />
            : <Briefcase size={10} style={{ color: agent.color, flexShrink: 0 }} />}
          <span style={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 9, color: 'rgba(15,15,30,0.55)', flexShrink: 0 }}>
            {hasVenture ? 'On' : (projectKind === 'build' ? 'Build' : 'Project')}
          </span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ventureLabel}</span>
        </div>
      )}
      {agent.tagline && (
        <p style={{ fontSize: 12, color: 'rgba(15,15,30,0.65)', lineHeight: 1.55, margin: '0 0 14px 0', minHeight: 36 }}>
          {agent.tagline}
        </p>
      )}
      {agent.skills?.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 14 }}>
          {agent.skills.slice(0, 4).map(s => (
            <span key={s} style={{
              fontSize: 10, padding: '3px 7px',
              background: 'rgba(15,15,30,0.035)', border: '1px solid rgba(15,15,30,0.06)',
              color: 'rgba(15,15,30,0.6)',
            }}>{s}</span>
          ))}
        </div>
      )}
      <button
        onClick={onToggle}
        disabled={!!busy || (isActive && isLocked)}
        data-testid={`core-toggle-${agent.agent_id}`}
        style={{
          width: '100%', padding: '9px 14px',
          background: isActive ? 'transparent' : 'linear-gradient(135deg, #7C3AED, #00A88C)',
          color: isActive ? 'rgba(15,15,30,0.72)' : '#000',
          border: isActive ? '1px solid rgba(15,15,30,0.1)' : 'none',
          fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
          cursor: busy || (isActive && isLocked) ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          transition: 'all 0.2s', letterSpacing: '0.02em',
        }}
      >
        {busy === 'activating' && 'Deploying…'}
        {busy === 'deactivating' && 'Removing…'}
        {!busy && isActive && isLocked && (<><Lock size={11} /> Core agent</>)}
        {!busy && isActive && !isLocked && (<><X size={12} /> Remove from team</>)}
        {!busy && !isActive && (<><Plus size={12} /> Deploy agent</>)}
      </button>
    </div>
  );
}

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
  gap: 12,
};

const emptyStyle = {
  gridColumn: '1 / -1',
  padding: '26px 0',
  color: 'rgba(15,15,30,0.45)',
  fontSize: 12,
  textAlign: 'center',
};


/* =================== Deploy modal (project picker) =================== */
function DeployModal({ agent, ventures, busy, onClose, onConfirm }) {
  const [projects, setProjects] = useState(null); // null = loading, [] = empty
  const [picked, setPicked] = useState(null);
  const isBusy = busy === 'activating';

  // Pull every assignable scope: builds, imports, and Monetize ventures. We
  // de-duplicate on project_id so a project that's also a venture appears
  // once with the venture metadata enriched (score, modules, first $).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/projects/all`, { credentials: 'include' });
        if (!r.ok) throw new Error('fetch failed');
        const d = await r.json();
        const items = Array.isArray(d.items) ? d.items : [];

        // Index ventures (Monetize) by project_id so we can attach score/etc.
        const ventureByPid = {};
        for (const v of (ventures || [])) {
          if (v.project_id) ventureByPid[v.project_id] = v;
        }

        const list = items
          .filter(it => it.kind === 'build' || it.kind === 'import')
          .map(it => {
            const pid = it.raw?.project_id || it.id;
            const v = ventureByPid[pid];
            return {
              project_id: pid,
              monetize_id: v?.monetize_id || null,
              kind: it.kind,
              name: it.title || pid,
              score: v?.score || 0,
              modules_complete: v?.modules_complete || [],
              first_dollar_at: v?.first_dollar_at || null,
              has_venture: !!v,
            };
          });

        if (!cancelled) {
          setProjects(list);
          setPicked(list[0]?.project_id || null);
        }
      } catch {
        if (!cancelled) setProjects([]);
      }
    })();
    return () => { cancelled = true; };
  }, [ventures]);

  const noProject = projects && projects.length === 0;
  const loading = projects === null;

  return (
    <div
      role="dialog"
      data-testid="deploy-modal"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 80,
        background: 'rgba(15,15,30,0.45)',
        backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'fadeIn 0.18s ease-out',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 'min(560px, 92vw)',
          background: '#FFFFFF',
          border: '1px solid rgba(15,15,30,0.08)',
          boxShadow: '0 24px 60px rgba(15,15,30,0.18), 0 1px 3px rgba(15,15,30,0.06)',
          borderRadius: 14,
          overflow: 'hidden',
          animation: 'fadeIn 0.22s ease-out',
        }}
      >
        {/* Header — agent identity + close */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14,
          padding: '20px 24px',
          background: `linear-gradient(135deg, ${agent.color}10, transparent)`,
          borderBottom: '1px solid rgba(15,15,30,0.06)',
        }}>
          <div style={{
            width: 44, height: 44,
            background: `${agent.color}1f`, color: agent.color,
            border: `1px solid ${agent.color}55`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: 15,
          }}>
            {agent.name.split(' ').map(w => w[0]).join('').slice(0, 2)}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(15,15,30,0.5)' }}>
              Deploy specialist
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#0F0F1A', letterSpacing: '-0.01em', marginTop: 2 }}>
              {agent.name} <span style={{ color: 'rgba(15,15,30,0.5)', fontWeight: 500 }}>·</span> <span style={{ fontWeight: 600, color: 'rgba(15,15,30,0.65)' }}>{agent.role}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="deploy-modal-close"
            aria-label="Close"
            style={{
              width: 30, height: 30, border: 'none',
              background: 'rgba(15,15,30,0.04)', color: 'rgba(15,15,30,0.6)',
              cursor: 'pointer', borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          ><X size={14} /></button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px 8px' }}>
          {agent.tagline && (
            <p style={{ fontSize: 13, lineHeight: 1.55, color: 'rgba(15,15,30,0.7)', margin: '0 0 18px' }}>
              {agent.tagline}
            </p>
          )}

          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 10,
          }}>
            <div style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
              color: 'rgba(15,15,30,0.5)',
            }}>Pick a project</div>
            {projects && projects.length > 0 && (
              <span style={{
                fontSize: 10, fontFamily: "'JetBrains Mono', monospace",
                color: 'rgba(15,15,30,0.5)',
              }}>
                {projects.length} project{projects.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {loading && (
            <div style={{
              padding: 18, textAlign: 'center',
              fontSize: 12, color: 'rgba(15,15,30,0.5)',
            }}>
              <Loader2 size={16} className="spin-anim" /> Loading your projects…
            </div>
          )}

          {noProject && (
            <div style={{
              padding: 18,
              background: 'rgba(255,215,0,0.08)',
              border: '1px dashed rgba(255,215,0,0.4)',
              fontSize: 12.5, lineHeight: 1.6, color: 'rgba(15,15,30,0.7)',
              borderRadius: 10,
            }}>
              You don&apos;t have any project yet. Start a build or import a repo to deploy {agent.name} on it.
            </div>
          )}

          {projects && projects.length > 0 && (
            <div
              data-testid="deploy-project-list"
              style={{
                display: 'flex', flexDirection: 'column', gap: 8,
                maxHeight: 320, overflowY: 'auto',
                paddingRight: 4,
              }}
            >
              {projects.map(p => {
                const active = p.project_id === picked;
                const kindLabel = p.kind === 'build' ? 'BUILD' : 'IMPORT';
                const kindColor = p.kind === 'build' ? '#A855F7' : '#3B82F6';
                return (
                  <button
                    key={p.project_id}
                    data-testid={`deploy-project-${p.project_id}`}
                    onClick={() => setPicked(p.project_id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '12px 14px',
                      width: '100%', textAlign: 'left',
                      background: active ? `${agent.color}12` : '#FCFBF8',
                      border: active ? `1.5px solid ${agent.color}` : '1px solid rgba(15,15,30,0.08)',
                      cursor: 'pointer', fontFamily: 'inherit',
                      transition: 'all 0.15s',
                      borderRadius: 10,
                      outline: 'none',
                    }}
                  >
                    <div style={{
                      width: 28, height: 28,
                      background: active ? agent.color : 'rgba(15,15,30,0.06)',
                      color: active ? '#FFFFFF' : 'rgba(15,15,30,0.55)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 800, fontSize: 11, borderRadius: 6,
                      flexShrink: 0,
                    }}>
                      {(p.name || 'P').slice(0, 1).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 13.5, fontWeight: 700, color: '#0F0F1A',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        display: 'flex', alignItems: 'center', gap: 8,
                      }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                        <span style={{
                          fontSize: 8.5, fontWeight: 700, letterSpacing: '0.1em',
                          padding: '2px 6px', flexShrink: 0,
                          color: kindColor,
                          background: `${kindColor}14`, border: `1px solid ${kindColor}38`,
                          fontFamily: "'JetBrains Mono', monospace",
                        }}>{kindLabel}</span>
                        {p.has_venture && (
                          <span style={{
                            fontSize: 8.5, fontWeight: 700, letterSpacing: '0.1em',
                            padding: '2px 6px', flexShrink: 0,
                            color: '#B8860B',
                            background: 'rgba(255,215,0,0.16)', border: '1px solid rgba(255,215,0,0.42)',
                            fontFamily: "'JetBrains Mono', monospace",
                          }}>VENTURE</span>
                        )}
                      </div>
                      <div style={{ fontSize: 10.5, color: 'rgba(15,15,30,0.55)', marginTop: 2, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        {p.has_venture ? (
                          <>
                            <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>score {p.score || 0}</span>
                            <span>·</span>
                            <span>{(p.modules_complete || []).length} modules</span>
                            {p.first_dollar_at && (
                              <>
                                <span>·</span>
                                <span style={{ color: '#22C55E', fontWeight: 700 }}>$ first dollar</span>
                              </>
                            )}
                          </>
                        ) : (
                          <span style={{ fontStyle: 'italic' }}>Not promoted to Monetize yet</span>
                        )}
                      </div>
                    </div>
                    {active && (
                      <div style={{
                        width: 18, height: 18, borderRadius: '50%',
                        background: agent.color, color: '#FFFFFF',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, fontWeight: 800, flexShrink: 0,
                      }}>✓</div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px 20px',
          display: 'flex', justifyContent: 'flex-end', gap: 10,
        }}>
          <button
            onClick={onClose}
            data-testid="deploy-modal-cancel"
            style={{
              padding: '10px 16px', fontFamily: 'inherit',
              background: 'transparent', color: 'rgba(15,15,30,0.7)',
              border: '1px solid rgba(15,15,30,0.12)',
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
              borderRadius: 8, letterSpacing: '0.02em',
            }}
          >Cancel</button>
          <button
            onClick={() => picked && onConfirm({ project_id: picked })}
            disabled={!picked || isBusy || noProject || loading}
            data-testid="deploy-modal-confirm"
            style={{
              padding: '10px 18px', fontFamily: 'inherit',
              background: !picked || noProject ? 'rgba(15,15,30,0.1)' : `linear-gradient(135deg, ${agent.color}, ${agent.color}cc)`,
              color: !picked || noProject ? 'rgba(15,15,30,0.4)' : '#0F0F1A',
              border: 'none',
              fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase',
              cursor: !picked || isBusy || noProject ? 'not-allowed' : 'pointer',
              borderRadius: 8,
              display: 'inline-flex', alignItems: 'center', gap: 6,
              boxShadow: !picked || noProject ? 'none' : `0 6px 18px ${agent.color}55`,
            }}
          >
            {isBusy ? 'Deploying…' : <>Deploy {agent.name} <ArrowRight size={12} /></>}
          </button>
        </div>
      </div>
    </div>
  );
}
